export const privacyPolicyContent = {
  title: "Privacy Policy",
  intro: "Welcome to the privacy policy of JLS Apps. This policy will help you understand what data we collect, why we collect it, and what your rights are in relation to it.",
  lastUpdate: "December 06, 2024",
  
  sections: [
    {
      id: "summary",
      title: "Summary",
      content: "This privacy policy describes how JLS Trading Co collects, uses, and shares your personal information when you use our services. We are committed to protecting your privacy and ensuring you have a positive experience on our website and with our services."
    },
    {
      id: "data-collection",
      title: "Data We Collect",
      content: `
        <p>Among the types of Personal Data that this Application collects, by itself or through third parties, there are:</p>
        <ul>
          <li>Usage Data</li>
          <li>Trackers</li>
          <li>Email address</li>
          <li>Username</li>
          <li>Data communicated while using the service</li>
        </ul>
        <p>Complete details on each type of Personal Data collected are provided in the dedicated sections of this privacy policy or by specific explanation texts displayed prior to the Data collection.</p>
        <p>Personal Data may be freely provided by the User, or, in case of Usage Data, collected automatically when using this Application.</p>
        <p>Unless specified otherwise, all Data requested by this Application is mandatory and failure to provide this Data may make it impossible for this Application to provide its services. In cases where this Application specifically states that some Data is not mandatory, Users are free not to communicate this Data without consequences to the availability or the functioning of the Service.</p>
        <p>Users who are uncertain about which Personal Data is mandatory are welcome to contact the Owner.</p>
        <p>Any use of Cookies – or of other tracking tools — by this Application or by the owners of third-party services used by this Application serves the purpose of providing the Service required by the User, in addition to any other purposes described in the present document and in the Cookie Policy.</p>
        <p>Users are responsible for any third-party Personal Data obtained, published or shared through this Application.</p>
      `
    },
    {
      id: "processing",
      title: "Mode and Place of Processing the Data",
      content: `
        <h3>Methods of Processing</h3>
        <p>The Owner takes appropriate security measures to prevent unauthorized access, disclosure, modification, or unauthorized destruction of the Data. The Data processing is carried out using computers and/or IT enabled tools, following organizational procedures and modes strictly related to the purposes indicated. In addition to the Owner, in some cases, the Data may be accessible to certain types of persons in charge, involved with the operation of this Application (administration, sales, marketing, legal, system administration) or external parties (such as third-party technical service providers, mail carriers, hosting providers, IT companies, communications agencies) appointed, if necessary, as Data Processors by the Owner. The updated list of these parties may be requested from the Owner at any time.</p>
        
        <h3>Place</h3>
        <p>The Data is processed at the Owner's operating offices and in any other places where the parties involved in the processing are located. Depending on the User's location, data transfers may involve transferring the User's Data to a country other than their own. To find out more about the place of processing of such transferred Data, Users can check the section containing details about the processing of Personal Data.</p>
        
        <h3>Retention Time</h3>
        <p>Unless specified otherwise in this document, Personal Data shall be processed and stored for as long as required by the purpose they have been collected for and may be retained for longer due to applicable legal obligation or based on the Users' consent.</p>
      `
    },
    {
      id: "purposes",
      title: "The Purposes of Processing",
      content: `
        <p>The Data concerning the User is collected to allow the Owner to provide its Service, comply with its legal obligations, respond to enforcement requests, protect its rights and interests (or those of its Users or third parties), detect any malicious or fraudulent activity, as well as the following:</p>
        <ul>
          <li>Hosting and backend infrastructure</li>
          <li>Registration and authentication</li>
          <li>Handling activities related to productivity</li>
        </ul>
      `
    },
    {
      id: "detailed-info",
      title: "Detailed Information on the Processing of Personal Data",
      content: `
        <h3>Handling Activities Related to Productivity</h3>
        <p>This type of service helps the Owner to manage tasks, collaboration and, in general, activities related to productivity. In using this type of service, Data of Users will be processed and may be retained, depending on the purpose of the activity in question. These services may be integrated with a wide range of third-party services disclosed within this privacy policy to enable the Owner to import or export Data needed for the relative activity.</p>
        
        <div>
          <h4>Google Sheets</h4>
          <p>Company: Google LLC</p>
          <p>Place of processing: United States</p>
          <p>Personal Data processed: Data communicated while using the service +2</p>
        </div>
        
        <div>
          <h4>Google Docs</h4>
          <p>Company: Google LLC</p>
          <p>Place of processing: United States</p>
          <p>Personal Data processed: Data communicated while using the service +1</p>
        </div>
        
        <h3>Hosting and Backend Infrastructure</h3>
        <p>This type of service has the purpose of hosting Data and files that enable this Application to run and be distributed as well as to provide a ready-made infrastructure to run specific features or parts of this Application. Some services among those listed below, if any, may work through geographically distributed servers, making it difficult to determine the actual location where the Personal Data are stored.</p>
        
        <div>
          <h4>Netlify</h4>
          <p>Company: Netlify, Inc.</p>
          <p>Place of processing: United States</p>
          <p>Personal Data processed: Usage Data</p>
        </div>
        
        <div>
          <h4>Firebase Cloud Firestore</h4>
          <p>Company: Google LLC</p>
          <p>Place of processing: United States</p>
          <p>Personal Data processed: Usage Data +1</p>
        </div>
        
        <div>
          <h4>Firebase Cloud Functions</h4>
          <p>Company: Google LLC</p>
          <p>Place of processing: United States</p>
          <p>Personal Data processed: Usage Data +1</p>
        </div>
        
        <h3>Registration and Authentication</h3>
        <p>By registering or authenticating, Users allow this Application to identify them and give them access to dedicated services. Depending on what is described below, third parties may provide registration and authentication services. In this case, this Application will be able to access some Data, stored by these third-party services, for registration or identification purposes. Some of the services listed below may also collect Personal Data for targeting and profiling purposes; to find out more, please refer to the description of each service.</p>
        
        <div>
          <h4>Google OAuth</h4>
          <p>Company: Google LLC</p>
          <p>Place of processing: United States</p>
          <p>Personal Data processed: Trackers +2</p>
        </div>
        
        <h3>Browser Extensions</h3>
        <p>We provide browser extensions to enhance user experience and productivity. These extensions operate with minimal data collection and are designed to protect user privacy.</p>
        
        <div>
          <h4>JLS Glossary Terms Chrome Extension</h4>
          <p>Company: JLS Trading Co</p>
          <p>Place of processing: Local browser storage and United States (Firebase Cloud Functions)</p>
          <p>Personal Data processed: None - Extension does not collect personal information</p>
          <p>Data handling: The extension only reads web page content locally to highlight glossary terms, stores user preferences (category filters) in local browser storage, and fetches glossary definitions from our secure Firebase database. No personal information, browsing history, or user activity is collected, stored, or transmitted to third parties.</p>
          <p>Permissions used:</p>
          <ul>
            <li><strong>Storage:</strong> To cache glossary terms locally for offline functionality and store user category preferences</li>
            <li><strong>ActiveTab:</strong> To scan and highlight glossary terms on the current web page only</li>
            <li><strong>Scripting:</strong> To inject content scripts for dynamic term highlighting and tooltip display</li>
            <li><strong>Host Permissions:</strong> To access websites for term highlighting and connect to our secure Firestore API</li>
          </ul>
          <p>The extension operates entirely locally within your browser and does not transmit any personal data or browsing information to external servers beyond fetching glossary term definitions from our secure database.</p>
        </div>
      `
    },
    {
      id: "eu-info",
      title: "Further Information for Users in the European Union",
      content: `
        <h3>Legal Basis of Processing</h3>
        <p>The Owner may process Personal Data relating to Users if one of the following applies:</p>
        <ul>
          <li>Users have given their consent for one or more specific purposes.</li>
          <li>Provision of Data is necessary for the performance of an agreement with the User and/or for any pre-contractual obligations thereof;</li>
          <li>Processing is necessary for compliance with a legal obligation to which the Owner is subject;</li>
          <li>Processing is related to a task that is carried out in the public interest or in the exercise of official authority vested in the Owner;</li>
          <li>Processing is necessary for the purposes of the legitimate interests pursued by the Owner or by a third party.</li>
        </ul>
        
        <p>In any case, the Owner will gladly help to clarify the specific legal basis that applies to the processing, and in particular whether the provision of Personal Data is a statutory or contractual requirement, or a requirement necessary to enter into a contract.</p>
        
        <h3>Further Information About Retention Time</h3>
        <p>Unless specified otherwise in this document, Personal Data shall be processed and stored for as long as required by the purpose they have been collected for and may be retained for longer due to applicable legal obligation or based on the Users' consent.</p>
        
        <p>Therefore:</p>
        <ul>
          <li>Personal Data collected for purposes related to the performance of a contract between the Owner and the User shall be retained until such contract has been fully performed.</li>
          <li>Personal Data collected for the purposes of the Owner's legitimate interests shall be retained as long as needed to fulfill such purposes. Users may find specific information regarding the legitimate interests pursued by the Owner within the relevant sections of this document or by contacting the Owner.</li>
        </ul>
        
        <p>The Owner may be allowed to retain Personal Data for a longer period whenever the User has given consent to such processing, as long as such consent is not withdrawn. Furthermore, the Owner may be obliged to retain Personal Data for a longer period whenever required to fulfil a legal obligation or upon order of an authority.</p>
        
        <p>Once the retention period expires, Personal Data shall be deleted. Therefore, the right of access, the right to erasure, the right to rectification and the right to data portability cannot be enforced after expiration of the retention period.</p>
      `
    },
    {
      id: "user-rights",
      title: "The Rights of Users Based on the General Data Protection Regulation (GDPR)",
      content: `
        <p>Users may exercise certain rights regarding their Data processed by the Owner.</p>
        
        <p>In particular, Users have the right to do the following, to the extent permitted by law:</p>
        <ul>
          <li>Withdraw their consent at any time. Users have the right to withdraw consent where they have previously given their consent to the processing of their Personal Data.</li>
          <li>Object to processing of their Data. Users have the right to object to the processing of their Data if the processing is carried out on a legal basis other than consent.</li>
          <li>Access their Data. Users have the right to learn if Data is being processed by the Owner, obtain disclosure regarding certain aspects of the processing and obtain a copy of the Data undergoing processing.</li>
          <li>Verify and seek rectification. Users have the right to verify the accuracy of their Data and ask for it to be updated or corrected.</li>
          <li>Restrict the processing of their Data. Users have the right to restrict the processing of their Data. In this case, the Owner will not process their Data for any purpose other than storing it.</li>
          <li>Have their Personal Data deleted or otherwise removed. Users have the right to obtain the erasure of their Data from the Owner.</li>
          <li>Receive their Data and have it transferred to another controller. Users have the right to receive their Data in a structured, commonly used and machine readable format and, if technically feasible, to have it transmitted to another controller without any hindrance.</li>
          <li>Lodge a complaint. Users have the right to bring a claim before their competent data protection authority.</li>
        </ul>
        
        <h3>Details About the Right to Object to Processing</h3>
        <p>Where Personal Data is processed for a public interest, in the exercise of an official authority vested in the Owner or for the purposes of the legitimate interests pursued by the Owner, Users may object to such processing by providing a ground related to their particular situation to justify the objection.</p>
        
        <p>Users must know that, however, should their Personal Data be processed for direct marketing purposes, they can object to that processing at any time, free of charge and without providing any justification. Where the User objects to processing for direct marketing purposes, the Personal Data will no longer be processed for such purposes. To learn whether the Owner is processing Personal Data for direct marketing purposes, Users may refer to the relevant sections of this document.</p>
        
        <h3>How to Exercise These Rights</h3>
        <p>Any requests to exercise User rights can be directed to the Owner through the contact details provided in this document. Such requests are free of charge and will be answered by the Owner as early as possible and always within one month, providing Users with the information required by law. Any rectification or erasure of Personal Data or restriction of processing will be communicated by the Owner to each recipient, if any, to whom the Personal Data has been disclosed unless this proves impossible or involves disproportionate effort. At the Users' request, the Owner will inform them about those recipients.</p>
      `
    },
    {
      id: "additional-info",
      title: "Additional Information About Data Collection and Processing",
      content: `
        <h3>Legal Action</h3>
        <p>The User's Personal Data may be used for legal purposes by the Owner in Court or in the stages leading to possible legal action arising from improper use of this Application or the related Services. The User declares to be aware that the Owner may be required to reveal personal data upon request of public authorities.</p>
        
        <h3>Additional Information About User's Personal Data</h3>
        <p>In addition to the information contained in this privacy policy, this Application may provide the User with additional and contextual information concerning particular Services or the collection and processing of Personal Data upon request.</p>
        
        <h3>System Logs and Maintenance</h3>
        <p>For operation and maintenance purposes, this Application and any third-party services may collect files that record interaction with this Application (System logs) or use other Personal Data (such as the IP Address) for this purpose.</p>
        
        <h3>Information Not Contained in This Policy</h3>
        <p>More details concerning the collection or processing of Personal Data may be requested from the Owner at any time. Please see the contact information at the beginning of this document.</p>
        
        <h3>Changes to This Privacy Policy</h3>
        <p>The Owner reserves the right to make changes to this privacy policy at any time by notifying its Users on this page and possibly within this Application and/or - as far as technically and legally feasible - sending a notice to Users via any contact information available to the Owner. It is strongly recommended to check this page often, referring to the date of the last modification listed at the bottom.</p>
        
        <p>Should the changes affect processing activities performed on the basis of the User's consent, the Owner shall collect new consent from the User, where required.</p>
      `
    },
    {
      id: "definitions",
      title: "Definitions and Legal References",
      content: `
        <h3>Personal Data (or Data)</h3>
        <p>Any information that directly, indirectly, or in connection with other information — including a personal identification number — allows for the identification or identifiability of a natural person.</p>
        
        <h3>Usage Data</h3>
        <p>Information collected automatically through this Application (or third-party services employed in this Application), which can include: the IP addresses or domain names of the computers utilized by the Users who use this Application, the URI addresses (Uniform Resource Identifier), the time of the request, the method utilized to submit the request to the server, the size of the file received in response, the numerical code indicating the status of the server's answer (successful outcome, error, etc.), the country of origin, the features of the browser and the operating system utilized by the User, the various time details per visit (e.g., the time spent on each page within the Application) and the details about the path followed within the Application with special reference to the sequence of pages visited, and other parameters about the device operating system and/or the User's IT environment.</p>
        
        <h3>User</h3>
        <p>The individual using this Application who, unless otherwise specified, coincides with the Data Subject.</p>
        
        <h3>Data Subject</h3>
        <p>The natural person to whom the Personal Data refers.</p>
        
        <h3>Data Processor (or Processor)</h3>
        <p>The natural or legal person, public authority, agency or other body which processes Personal Data on behalf of the Controller, as described in this privacy policy.</p>
        
        <h3>Data Controller (or Owner)</h3>
        <p>The natural or legal person, public authority, agency or other body which, alone or jointly with others, determines the purposes and means of the processing of Personal Data, including the security measures concerning the operation and use of this Application. The Data Controller, unless otherwise specified, is the Owner of this Application.</p>
        
        <h3>This Application</h3>
        <p>The means by which the Personal Data of the User is collected and processed.</p>
        
        <h3>Service</h3>
        <p>The service provided by this Application as described in the relative terms (if available) and on this site/application.</p>
        
        <h3>European Union (or EU)</h3>
        <p>Unless otherwise specified, all references made within this document to the European Union include all current member states to the European Union and the European Economic Area.</p>
        
        <h3>Cookie</h3>
        <p>Cookies are Trackers consisting of small sets of data stored in the User's browser.</p>
        
        <h3>Tracker</h3>
        <p>Tracker indicates any technology - e.g Cookies, unique identifiers, web beacons, embedded scripts, e-tags and fingerprinting - that enables the tracking of Users, for example by accessing or storing information on the User's device.</p>
        
        <h3>Legal Information</h3>
        <p>This privacy policy relates solely to this Application, if not stated otherwise within this document.</p>
      `
    },
    {
      id: "contact",
      title: "Contact Information",
      content: `
        <p>If you have any questions about this Privacy Policy, please contact us at:</p>
        <p><strong>JLS Trading Co</strong></p>
        <p>5301 Terminal Street</p>
        <p>Charlotte NC 28208</p>
        <p><strong>Email:</strong> <a href="mailto:rdhpndh@gmail.com">rdhpndh@gmail.com</a></p>
        <p><strong>Phone:</strong> <a href="tel:8002091869">800-209-1869</a></p>
      `
    }
  ],
  
  ownerInfo: {
    name: "JLS Trading Co",
    address: "5301 Terminal Street",
    city: "Charlotte NC 28208",
    email: "rdhpndh@gmail.com"
  }
};
